import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import { GraduationCap, LogOut, Menu, X, ChevronRight } from "lucide-react";
import { useState } from "react";

const NAVY = "#0A1230";

export default function Shell({ nav, active, setActive, secondNav, secondActive, setSecondActive, children, subtitle }) {
  const { user, logout } = useAuth();
  const role = ROLES[user?.role] || {};
  const [open, setOpen] = useState(false);

  const Item = ({ n, isActive, onClick }) => {
    const Icon = n.icon;
    return (
      <button
        onClick={() => {
          onClick(n.id);
          setOpen(false);
        }}
        data-testid={`nav-${n.id}`}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left group ${
          isActive ? "bg-green-600 text-white shadow-md shadow-green-900/40" : "text-slate-300 hover:text-white hover:bg-white/10"
        }`}
      >
        <span className={`flex items-center justify-center w-7 h-7 rounded-md ${isActive ? "bg-white/20" : "bg-white/5 group-hover:bg-white/15"}`}>
          <Icon size={15} />
        </span>
        <span className="flex-1">{n.label}</span>
        {isActive && <ChevronRight size={14} className="opacity-70" />}
      </button>
    );
  };

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 px-5 pt-6 pb-5 border-b border-white/10">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-sm">
          <GraduationCap className="text-white" size={20} />
        </div>
        <div>
          <div className="font-display font-extrabold text-[15px] text-white leading-tight">TYT/AYT Takip</div>
          <div className="text-[11px] text-slate-400">{subtitle}</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
        <div>
          <div className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Ana Menü</div>
          <div className="space-y-0.5">
            {nav.map((n) => (
              <Item key={n.id} n={n} isActive={active === n.id} onClick={setActive} />
            ))}
          </div>
        </div>
        {secondNav && secondNav.length > 0 && (
          <div>
            <div className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Çalışma Alanı</div>
            <div className="space-y-0.5">
              {secondNav.map((n) => (
                <Item key={n.id} n={n} isActive={secondActive === n.id} onClick={setSecondActive} />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="px-3 py-3 border-t border-white/10">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-slate-200">
          <span className="w-2 h-2 rounded-full bg-green-400" />
          <span className="text-xs font-semibold">{role.label} Paneli</span>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden lg:flex w-64 flex-col shrink-0" style={{ background: NAVY }}>
        <SidebarContent />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 flex flex-col" style={{ background: NAVY }}>
            <div className="flex items-center justify-between px-5 pt-6 pb-3 border-b border-white/10">
              <span className="font-display font-bold text-white">Menü</span>
              <button onClick={() => setOpen(false)}>
                <X className="text-slate-300" size={20} />
              </button>
            </div>
            <div className="flex-1 flex flex-col min-h-0">
              <SidebarContent />
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-slate-200 bg-white flex items-center justify-between px-4 sm:px-6 sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle">
              <Menu className="text-slate-600" size={22} />
            </button>
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm font-medium text-slate-500">{subtitle}</span>
              <span className="text-slate-300">/</span>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${role.badge}`} data-testid="role-badge">
                {role.label}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <NotificationBell />
            <div className="hidden md:block text-right">
              <div className="text-sm font-semibold text-slate-800 leading-tight">{user?.name}</div>
              <div className="text-[11px] text-slate-400">{user?.email}</div>
            </div>
            <div className="hidden md:flex w-9 h-9 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 text-white items-center justify-center text-sm font-bold">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-500 hover:text-rose-600" data-testid="logout-btn">
              <LogOut size={18} />
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
